import React from 'react';
import { useTranslation } from 'react-i18next';
import TuneIcon from '@mui/icons-material/Tune';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import BlockIcon from '@mui/icons-material/Block';
import ShieldIcon from '@mui/icons-material/Shield';
import { useTheme } from '../../../theme';
import '../../../styles/Page2026.css';

function Security2026() {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const securitySteps = [
    {
      title: t('home:security.step1Title', 'Encrypted on your device first'),
      body: t(
        'home:security.step1Body',
        'Every file is locked with AES-256 on your phone or computer before it goes anywhere. Only you hold the key.'
      ),
    },
    {
      title: t('home:security.step2Title', 'Encrypted the whole way'),
      body: t(
        'home:security.step2Body',
        'Your files travel encrypted and stay encrypted in storage. Even we only ever see scrambled data.'
      ),
    },
    {
      title: t('home:security.step3Title', 'Verified every 30 days'),
      body: t(
        'home:security.step3Body',
        "A one-time passcode confirms it's still you. A stolen password alone won't open your vault."
      ),
    },
    {
      title: t('home:security.step4Title', 'Every access, on the record'),
      body: t(
        'home:security.step4Body',
        'See every login and every folder opened, with the date, time, and IP address.'
      ),
    },
  ];

  const otpDigits = ['4', '8', '1', '9', '2', '7'];

  const accessLogs = [
    {
      action: t('home:security.diagram.log1Action', 'Opened “Passport & ID”'),
      time: t('home:security.diagram.log1Time', 'Today, 9:41 AM'),
      ip: '72.184.21.9',
      isNew: true,
    },
    {
      action: t('home:security.diagram.log2Action', 'Signed in'),
      time: t('home:security.diagram.log2Time', 'Today, 9:40 AM'),
      ip: '72.184.21.9',
      isNew: false,
    },
    {
      action: t('home:security.diagram.log3Action', 'Opened “Insurance”'),
      time: t('home:security.diagram.log3Time', 'Sep 24, 6:12 PM'),
      ip: '72.184.21.9',
      isNew: false,
    },
  ];

  const securityFeatures = [
    {
      icon: <TuneIcon fontSize="small" />,
      title: t('home:security.features.f1Title', 'Private access controls'),
      text: t(
        'home:security.features.f1Text',
        'You decide who sees what. Permissions granted, restricted, or revoked at any time.'
      ),
    },
    {
      icon: <Inventory2Icon fontSize="small" />,
      title: t('home:security.features.f2Title', 'Your data, always portable'),
      text: t(
        'home:security.features.f2Text',
        'Export everything anytime. Your files are never held hostage by our platform.'
      ),
    },
    {
      icon: <BlockIcon fontSize="small" />,
      title: t('home:security.features.f3Title', 'Zero access. Zero ads.'),
      text: t(
        'home:security.features.f3Text',
        'Your information is never shared with third parties. Only you have access to your files.'
      ),
    },
    {
      icon: <ShieldIcon fontSize="small" />,
      title: t('home:security.features.f4Title', 'Military-grade standard'),
      text: t(
        'home:security.features.f4Text',
        'AES-256 encryption – the same standard used by governments and intelligence agencies.'
      ),
    },
  ];

  return (
    <div className="zk-section-wrapper" id="security-wrapper" data-theme={theme}>
      <section
        className="security-2026 zk"
        id="security"
        data-reveal
        aria-labelledby="zk-title"
      >
        {/* ---------- Left column ---------- */}
        <div>
          <p className="zk-eyebrow">
            {t('home:security.tag', 'Zero-knowledge architecture')}
          </p>

          <h2 className="zk-title" id="zk-title">
            {t('home:security.title', 'Your files are encrypted')}{' '}
            {t('home:security.highlight', 'before they leave your device.')}
          </h2>

          <ol className="zk-steps">
            {securitySteps.map((step, i) => (
              <li key={i} className="zk-step">
                <span className="zk-num">{i + 1}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* ---------- Right panel ---------- */}
        <aside
          className="zk-panel"
          aria-label={t('home:security.diagram.heading', 'How your data travels')}
        >
          <p className="zk-panel-title">
            {t('home:security.diagram.heading', 'How your data travels')}
          </p>

          {/* 1. Node: Your device */}
          <div className="node">
            <div className="node-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="5" y="2" width="14" height="20" rx="2.5" />
                <path d="M12 18h.01" />
              </svg>
            </div>
            <div>
              <h4>{t('home:security.diagram.device1', 'Your device')}</h4>
              <p>
                {t(
                  'home:security.diagram.device1Sub',
                  'AES-256 encryption happens here'
                )}
              </p>
            </div>
          </div>

          {/* Link 1: Ciphertext */}
          <div className="link" aria-hidden="true">
            <span className="cipher">x9#Qe7!mK2$vR8</span>
          </div>

          {/* 2. Node: BeforeVault storage */}
          <div className="node">
            <div className="node-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="4" y="10" width="16" height="11" rx="2" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              </svg>
            </div>
            <div>
              <h4>
                {t('home:security.diagram.storage', 'BeforeVault storage')}
              </h4>
              <p>
                {t(
                  'home:security.diagram.storageSub',
                  'Stored encrypted. Unreadable to us.'
                )}
              </p>
            </div>
          </div>

          {/* Link 2: Ciphertext */}
          <div className="link" aria-hidden="true">
            <span className="cipher">k4@Lp0&zW6!tN1</span>
          </div>

          {/* 3. Node: Is it you? (OTP) */}
          <div className="node">
            <div className="node-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            <div>
              <h4>{t('home:security.diagram.isItYou', 'Is it you?')}</h4>
              <div
                className="otp"
                aria-label={t(
                  'home:security.diagram.otpAria',
                  'Six-digit one-time passcode'
                )}
              >
                {otpDigits.map((digit, idx) => (
                  <span key={idx}>{digit}</span>
                ))}
              </div>
              <p className="otp-note">
                {t(
                  'home:security.diagram.otpNote',
                  'One-time passcode every 30 days'
                )}
              </p>
            </div>
          </div>

          {/* Link 3: Ciphertext */}
          <div className="link" aria-hidden="true">
            <span className="cipher">f2*Hs5#bY9%cJ3</span>
          </div>

          {/* 4. Node: Your device again */}
          <div className="node">
            <div className="node-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="4" y="10" width="16" height="11" rx="2" />
                <path d="M8 10V7a4 4 0 0 1 7.5-2" />
              </svg>
            </div>
            <div>
              <h4>
                {t('home:security.diagram.device2', 'Your device again')}
              </h4>
              <p>
                {t(
                  'home:security.diagram.device2Sub',
                  'Decryption happens here, nowhere else'
                )}
              </p>
            </div>
          </div>

          {/* Access History */}
          <div
            className="log"
            role="table"
            aria-label={t(
              'home:security.diagram.accessHistoryAria',
              'Example access history'
            )}
          >
            <div className="log-head">
              <h4>
                {t('home:security.diagram.accessHistory', 'Your access history')}
              </h4>
              <small>
                {t(
                  'home:security.diagram.accessHistorySub',
                  'Yours to review anytime'
                )}
              </small>
            </div>
            {accessLogs.map((row, idx) => (
              <div
                key={idx}
                className={`log-row ${row.isNew ? 'new' : ''}`}
                role="row"
              >
                <div>
                  <div className="what">{row.action}</div>
                  <div className="meta">
                    {row.time} &nbsp;<span className="ip">IP {row.ip}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Guarantee */}
          <div className="guarantee">
            <h4>
              {t(
                'home:security.diagram.guarantee',
                'Zero-knowledge guarantee'
              )}
            </h4>
            <p>
              {t(
                'home:security.diagram.guaranteeText',
                "We can't read your files. Only you can."
              )}{' '}
              <em>
                {t(
                  'home:security.diagram.guaranteeBold',
                  'Not now. Not ever.'
                )}
              </em>
            </p>
          </div>
        </aside>
      </section>

      {/* ---------- Bottom Features Cards ---------- */}
      <div className="zk-features-container">
        <div className="zk-features-grid">
          {securityFeatures.map((f, idx) => (
            <div key={idx} className="zk-feature-card">
              <div className="zk-feature-icon">{f.icon}</div>
              <h4>{f.title}</h4>
              <p>{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Security2026;
